import { EntityTypeName } from "@odata/GeneratedEntityTypes";
import { OData } from "@odata/OData";
import { memoizeOne } from "@utils/memoizeOne";
import i18next from "i18next";
import React from "react";
import { WithTranslation } from "react-i18next";

import { NEW_ITEM_DETAIL } from "../constants";
import { AppContext, IAppContext } from "../contexts/appContext/AppContext.types";
import { PageViewMode } from "../enums";
import { RequireKeys, TRecordAny } from "../global.types";
import { StorageModel } from "../model/StorageModel";
import BindingContext, { createBindingContext } from "../odata/BindingContext";
import { IFormDef } from "../views/formView/Form";
import { getEntitySetFromDefinition } from "../views/formView/Form.utils";
import { ISplitPageTableDef } from "../views/table/TableView.utils";
import { ISplitPagePaneData } from "./DialogPage";
import { getDefByEntityType } from "./getDefByEntityType";
import { IDefinition } from "./PageUtils";

export interface IDialogPageBaseProps {
    getDef: (context?: IAppContext, ...args: any[]) => any;
    rootStorage?: StorageModel;
    oData?: OData;
    t?: WithTranslation["t"];
    customFooterButtons?: React.ReactElement;
    pageViewMode?: PageViewMode;
    onAfterSave?: (bc: BindingContext) => void;

    // To customize form definition creation, you can pass these args,
    // they will be used as second parameter of getDef function call
    getDefArgs?: TRecordAny;
}

export default abstract class DialogPageBase<P extends IDialogPageBaseProps, S, T> extends React.Component<P, S> {
    static contextType = AppContext;
    //sadly, breaks typescript type checking
    //context: React.ContextType<typeof AppContext>;
    static defaultProps: RequireKeys<Partial<IDialogPageBaseProps>, "getDef">;

    protected definition: IDefinition;
    protected currentEntityType: string;


    protected masterData: ISplitPagePaneData<ISplitPageTableDef> = {};
    protected detailData: ISplitPagePaneData<IFormDef> = {};

    constructor(props: P, context: IAppContext) {
        super(props, context);

        const args = this.props.getDefArgs ?? {};
        this.definition = this.props.getDef(context, args);
        this.masterData.def = this.definition.table;

        this.bindEvents();
    }

    get oData(): OData {
        return this.props.rootStorage?.oData ?? this.props.oData;
    }

    get t(): WithTranslation["t"] {
        return this.props.rootStorage?.t ?? this.props.t;
    }

    bindEvents = (): void => {
        this.handleRowSelect = this.handleRowSelect.bind(this);
        this.handleCloseDetail = this.handleCloseDetail.bind(this);
        this.handleAfterSave = this.handleAfterSave.bind(this);
    };

    getEntitySet(): string {
        return getEntitySetFromDefinition(this.definition, this.context);
    }

    getTableViewContext = memoizeOne(() =>
                    createBindingContext(this.getEntitySet(), this.oData.getMetadata()),
            () => [this.getEntitySet()]);

    getChildCollectionForEditableParent = (): string => {
        return undefined;
    };

    setFormDefFromEntityType = async (bc: BindingContext): Promise<boolean> => {
        if (bc) {
            const bcs = bc.getFullPathAsArrayOfContexts();
            if (bcs?.length > 0) {
                const entityType: string = bcs[bcs.length - 1].getEntityType().getName();
                if (entityType !== this.currentEntityType) {
                    this.currentEntityType = entityType;
                    const definition = getDefByEntityType(entityType as EntityTypeName);
                    await i18next.loadNamespaces(definition.translationFiles);
                    const args = this.props.getDefArgs ?? {};
                    this.detailData.def = definition(this.context, args).form;

                    return true;
                }
            }
        }

        return false;
    };

    getMandatoryChildParentProps = (): TRecordAny => {
        return {};
    };

    handleRowSelect(bc: BindingContext): void {
        this.setFormDefFromEntityType(bc)
            .then(() => {
                // keep it here, so bc always corresponds with definition
                this.detailData.bc = bc;
                this.forceUpdate();
            });
    }

    handleCloseDetail(): void {
        this.detailData.bc = null;
        this.forceUpdate();
    }

    handleAfterSave(bc: BindingContext): void {
        this.detailData.bc = bc;
        this.props.onAfterSave?.(bc);
        this.forceUpdate();
    }

    handleAddDetail = (type?: string): void => {
        const bc = createBindingContext(type ?? this.getEntitySet(), this.oData.metadata).addKey(NEW_ITEM_DETAIL, true);
        this.handleRowSelect?.(bc);
    };

    abstract getMandatoryProps(): T & { key: string };

    abstract render(): React.ReactElement;
}